import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * AutocompleteSelect - A searchable multi-select component with tag chips
 *
 * Props:
 *   options: Array<{ id: string, label: string, group?: string }>
 *   selected: Array<string>  - array of selected option IDs
 *   onChange: (selectedIds: string[]) => void
 *   placeholder: string
 *   groupLabel: string
 *   emptyText: string
 *   maxHeight: string
 */
const AutocompleteSelect = ({
  options,
  selected = [],
  onChange,
  placeholder = 'Type to search...',
  emptyText = 'No options found',
  maxHeight = 'max-h-48',
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search query
  const filtered = query.trim()
    ? options.filter(opt =>
        opt.label.toLowerCase().includes(query.toLowerCase().trim())
      )
    : options;

  // Group options if they have a group property
  const grouped = {};
  for (const opt of filtered) {
    const group = opt.group || '';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(opt);
  }
  const groupKeys = Object.keys(grouped);

  const toggleOption = useCallback((optionId) => {
    const newSelected = selected.includes(optionId)
      ? selected.filter(id => id !== optionId)
      : [...selected, optionId];
    onChange(newSelected);
    // Keep focus on input after selection
    inputRef.current?.focus();
  }, [selected, onChange]);

  const removeOption = useCallback((optionId, e) => {
    e.stopPropagation();
    onChange(selected.filter(id => id !== optionId));
    inputRef.current?.focus();
  }, [selected, onChange]);

  const clearAll = useCallback((e) => {
    e.stopPropagation();
    onChange([]);
    inputRef.current?.focus();
  }, [onChange]);

  // Get label for a selected ID
  const getLabel = (id) => {
    const opt = options.find(o => o.id === id);
    return opt ? opt.label : id;
  };

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Tag chips + search input */}
      <div
        className="w-full border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-primary-500 bg-white cursor-text min-h-[42px]"
        onClick={() => inputRef.current?.focus()}
      >
        <div className="flex flex-wrap gap-1.5 p-1.5">
          {selected.map(id => (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium"
            >
              {getLabel(id)}
              <button
                type="button"
                onClick={(e) => removeOption(id, e)}
                className="text-primary-500 hover:text-primary-700 focus:outline-none"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            placeholder={selected.length === 0 ? placeholder : ''}
            className="flex-1 min-w-[120px] outline-none border-none bg-transparent text-sm py-1 px-1"
          />
        </div>
        {/* Clear all button */}
        {selected.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="absolute top-2 right-2.5 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg ${maxHeight} overflow-y-auto`}>
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              {emptyText}
            </div>
          ) : (
            groupKeys.map(groupKey => {
              const groupOptions = grouped[groupKey];
              const allGroupIds = groupOptions.map(o => o.id);
              const allGroupSelected = allGroupIds.every(id => selected.includes(id));
              const someGroupSelected = allGroupIds.some(id => selected.includes(id));

              const handleGroupToggle = () => {
                if (allGroupSelected) {
                  // Remove all items from this group
                  onChange(selected.filter(id => !allGroupIds.includes(id)));
                } else {
                  // Add all items from this group
                  const existing = selected.filter(id => !allGroupIds.includes(id));
                  onChange([...existing, ...allGroupIds]);
                }
                inputRef.current?.focus();
              };

              return (
                <div key={groupKey}>
                  {groupKey && (
                    <button
                      type="button"
                      onClick={handleGroupToggle}
                      className="w-full flex items-center gap-3 px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider sticky top-0 hover:bg-gray-100 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={allGroupSelected}
                        ref={el => { if (el) el.indeterminate = someGroupSelected && !allGroupSelected; }}
                        onChange={() => {}}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 pointer-events-none"
                      />
                      <span className="flex-1 text-left">{groupKey}</span>
                      <span className="text-xs font-normal text-gray-400">
                        {allGroupSelected ? 'Deselect all' : 'Select all'}
                      </span>
                    </button>
                  )}
                  {groupOptions.map(opt => {
                    const isSelected = selected.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleOption(opt.id)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-primary-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 pointer-events-none"
                        />
                        <span className={`text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default AutocompleteSelect;